import BlockApi from '@/api/api';
import SearchField from '@/components/common/SearchField';
import FolderTreeView from '@/components/navigation/folder/FolderTreeView';
import ApplicationDetails from '@/components/navigation/wizard/application/ApplicationDetails';
import { useConfig } from '@/hooks/useConfig';
import { useDialogs } from '@/hooks/useDialogs';
import { BackendApplication } from '@/types/backend';
import { Folder } from '@/types/folder';
import { applicationCustomTooltip, applicationSearchOptions, sortBy } from '@/util/application';
import { filterApplicationsInFolders, findApplicationPath, mergeFoldersAndApplications } from '@/util/folder';
import { Box, CircularProgress } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { FC, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useLocalStorage } from 'usehooks-ts';

type LoadApplicationDialogProps = {
  open: boolean;
  onClose: () => void;
  loadApplication: (application: BackendApplication) => void;
  title?: string;
  confirmLabel?: string;
};

const LoadApplicationDialog: FC<LoadApplicationDialogProps> = (props) => {
  const { config } = useConfig();
  const auth = useAuth();
  const { showError } = useDialogs();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Folder[]>();
  const [filteredData, setFilteredData] = useState<Folder[]>();
  const [selectedApplication, setSelectedApplication] = useState<BackendApplication>();

  const [searchQuery, setSearchQuery] = useState('');
  const [sortAppsBy, setSortAppsBy] = useLocalStorage('loadAppSort', 'label');
  const [invert, setInvert] = useState(false);

  const getData = async () => {
    if (!auth.user?.access_token) return;
    try {
      const folders = await BlockApi(config.apiUrl, auth.user.access_token).folder.list();
      const applications = await BlockApi(config.apiUrl, auth.user.access_token).application.list();

      if (folders && applications) {
        const newData = mergeFoldersAndApplications(
          sortBy('name', folders, invert),
          sortBy(sortAppsBy, applications, invert)
        );
        setData(newData);
        setFilteredData(searchQuery ? filterApplicationsInFolders(newData, searchQuery, sortAppsBy, invert) : newData);
      }

      setLoading(false);
    } catch (error) {
      showError({
        title: 'An error has occured',
        message: 'An error occured while fetching folders and applications',
        stacktrace: error
      });
    }
  };

  const handleLoad = (application?: BackendApplication) => {
    if (application) props.loadApplication(application);
    else if (selectedApplication) props.loadApplication(selectedApplication);

    setSearchQuery('');
    props.onClose();
  };

  useEffect(() => {
    if (!data) return;
    setFilteredData(filterApplicationsInFolders(data, searchQuery, sortAppsBy, invert));
  }, [searchQuery, sortAppsBy, invert]);

  useEffect(() => {
    if (props.open) {
      setLoading(true);
      getData();
    } else if (!props.open) {
      setSelectedApplication(undefined);
    }
  }, [props.open]);

  return (
    <Dialog open={props.open} onClose={props.onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        {props.title ?? 'Load'}
        <Box mt={1} display='flex' flexDirection='column' alignItems='center'>
          <SearchField
            sortBy={sortAppsBy}
            invert={invert}
            searchOptions={applicationSearchOptions}
            onSort={(changeSortBy, reverse) => {
              changeSortBy && setSortAppsBy(changeSortBy);
              reverse && setInvert(!invert);
            }}
            searchQuery={searchQuery}
            setSearchQuery={(query) => setSearchQuery(query)}
            customTooltip={applicationCustomTooltip}
            autoFocus={props.open}
          />
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box flexGrow={8} sx={{ textAlign: 'center', placeSelf: 'center', mt: 10 }}>
            <CircularProgress color='inherit' />
          </Box>
        ) : (
          <FolderTreeView
            folders={filteredData}
            onFolderClick={() => setSelectedApplication(undefined)}
            onFolderDoubleClick={() => setSelectedApplication(undefined)}
            onApplicationClick={(application) => setSelectedApplication(application)}
            onApplicationDoubleClick={(application) => handleLoad(application)}
          />
        )}

        <ApplicationDetails
          application={selectedApplication}
          path={
            selectedApplication && selectedApplication.applicationId
              ? findApplicationPath(filteredData, selectedApplication.applicationId)
              : undefined
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose} color='primary'>
          Cancel
        </Button>
        <Button variant='contained' disabled={!selectedApplication} onClick={() => handleLoad()} color='primary'>
          {props.confirmLabel ?? 'Load'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LoadApplicationDialog;
